import React from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { pageHeader, pageTitleProps, pageSubtitle } from '../../styles/pageStyles';
import { useThemeLabels } from '../../hooks/useThemeLabels';
import { getIconClass } from '../../utils/iconUtils';
import ContextualHint from '../ContextualHint';

/**
 * PageLayout — Componente template padrão de TODA página da aplicação.
 *
 * Encapsula o cabeçalho da página (título, subtítulo, ícone, botão de ação),
 * a dica contextual e o banner informativo opcional. Garante consistência
 * visual estrutural — toda página DEVE usar este componente.
 *
 * Regras estruturais (não negociáveis, aplicadas internamente):
 *  - Título sempre h4, fontWeight bold, color primary.main
 *  - Título sempre acompanhado pelo ícone do tema (à esquerda)
 *  - Subtítulo sempre body2, color text.secondary
 *  - Header sempre usa pageHeader sx (flex space-between, mb: 2)
 *  - Hint sempre via ContextualHint padrão (nunca inline)
 *
 * @param {string}  themeKey       Chave do tema usada para getLabel(key), getLabel(key+'Description') e getIcon(key). Opcional se title/subtitle/icon forem fornecidos.
 * @param {string}  title          Título da página. Sobrepõe getLabel(themeKey) quando fornecido.
 * @param {string}  subtitle       Subtítulo/descrição. Sobrepõe getLabel(themeKey+'Description') quando fornecido.
 * @param {React.Element} icon     Ícone customizado (sobrepõe ícone do tema).
 * @param {string}  actionLabel    Label do botão de ação no canto superior direito.
 * @param {React.Element} actionIcon Ícone do botão de ação. Default: <AddIcon />.
 * @param {function} onAction      Callback de clique no botão de ação.
 * @param {boolean} actionDisabled Desabilita o botão de ação.
 * @param {React.Element} actionButton Override completo do botão (escape hatch).
 * @param {object}  hint           Objeto de hint retornado por useContextualHint.
 * @param {function} onHintAction  Callback opcional para ação da hint.
 * @param {string|React.Element} infoBanner Texto/elemento renderizado dentro de Alert severity="info".
 * @param {React.ReactNode} children Conteúdo da página.
 */
export default function PageLayout({
  themeKey,
  title,
  subtitle,
  icon,
  actionLabel,
  actionIcon,
  onAction,
  actionDisabled = false,
  actionButton,
  hint,
  onHintAction,
  infoBanner,
  children,
}) {
  const { getLabel, getIcon } = useThemeLabels();

  // Resolve título e subtítulo: prop > tema > vazio
  const resolvedTitle = title || (themeKey ? getLabel(themeKey) : '');
  const resolvedSubtitle =
    subtitle !== undefined
      ? subtitle
      : themeKey
      ? getLabel(`${themeKey}Description`)
      : '';

  // Resolve ícone: prop customizado > ícone do tema (sempre exibido)
  let resolvedIcon = null;
  if (icon) {
    resolvedIcon = icon;
  } else if (themeKey) {
    const iconConfig = getIcon(themeKey);
    if (iconConfig?.name) {
      const IconComponent = getIconClass(iconConfig.name);
      resolvedIcon = (
        <IconComponent
          sx={{
            mr: 1,
            verticalAlign: 'middle',
            fontSize: 32,
            color: 'primary.main',
          }}
        />
      );
    }
  }

  // Resolve botão de ação
  let resolvedActionButton = null;
  if (actionButton) {
    resolvedActionButton = actionButton;
  } else if (actionLabel && onAction) {
    resolvedActionButton = (
      <Button
        variant="contained"
        startIcon={actionIcon || <AddIcon />}
        onClick={onAction}
        disabled={actionDisabled}
      >
        {actionLabel}
      </Button>
    );
  }

  return (
    <Box>
      <Box sx={pageHeader}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography {...pageTitleProps}>
            {resolvedIcon}
            {resolvedTitle}
          </Typography>
          {resolvedSubtitle && (
            <Typography {...pageSubtitle}>{resolvedSubtitle}</Typography>
          )}
        </Box>
        {resolvedActionButton}
      </Box>

      {hint && <ContextualHint hint={hint} onActionClick={onHintAction} />}

      {infoBanner && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {infoBanner}
        </Alert>
      )}

      {children}
    </Box>
  );
}
